import { BlogCard } from './types';

export const nutritionBlogCards: BlogCard[] = [
  {
    title: {
      content:
        '### Bone health and menopause: what you can do to protect your skeleton now',
      styles: {
        h3: 'font-Mulish font-bold text-royal-875 my-3 text-base md:text-lg leading-tight line-clamp-2',
      },
      topicsStyles: {
        h3: 'text-theme-primary text-3xl md:text-4xl my-4 md:my-6 p-2 font-black',
      },
    },
    description: {
      content:
        'Bone loss accelerates during menopause, but with the right approach to nutrition, exercise and assessment, the rate of decline can be meaningfully slowed',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    imageUrl: 'osteoporosis-occur.webp',
    bannerUrl: 'osteoporosis-occur.webp',
    slug: 'bone-health-and-menopause',
    contentType: 'article',
    speciality: 'Nutritional Therapy',
    iconUrl: 'monica-durigon.webp',
    doctorSlug: 'monica-durigon',
    date: {
      content: '03, September 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    postimonial: {
      content: 'PhD, MRCOG, PGCert \n Consultant Obstetrician & Gynaecologist',
      styles: {
        p: 'text-xl whitespace-pre-line',
      },
    },
    author: {
      content: 'Dr Monica Durigon',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    bannerDescription: `Bone loss accelerates during menopause, but with the right approach to nutrition, exercise and assessment, the rate of decline can be meaningfully slowed. From perimenopause onwards, falling oestrogen levels disrupt the balance between bone breakdown and bone formation, a process that often happens without any symptoms at all. Because bone loss is silent until a fracture occurs, understanding your personal risk and acting early makes a significant difference. This article explains what happens to bone during the menopausal transition, how a bone density scan in London can help assess your risk, and what you can do nutritionally and physically to protect your skeleton for the years ahead.`,
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'What happens to bone during menopause?',
          id: 'what-happens-to-bone-during-menopause',
        },
        {
          text: 'Who is most at risk of bone loss around menopause?',
          id: 'who-is-most-at-risk-of-bone-loss-around-menopause',
        },
        {
          text: 'Bone density scanning in London: how LIPS Healthcare assesses your bone health',
          id: 'bone-density-scanning-in-london',
        },
        {
          text: 'Calcium, vitamin D and protein: the nutritional foundations of bone health',
          id: 'calcium-vitamin-d-and-protein-the-nutritional-foundations-of-bone-health',
        },
        {
          text: 'Why your bones need exercise as much as nutrition',
          id: 'why-your-bones-need-exercise-as-much-as-nutrition',
        },
        {
          text: 'Frequently asked questions about bone health and menopause',
          id: 'frequently-asked-questions',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    content: {
      content: `

<span id="what-happens-to-bone-during-menopause"></span>

### What happens to bone during menopause?

Bone is living tissue that is constantly being remodelled. Specialised cells called osteoclasts break down older bone, while osteoblasts build new bone in its place. Oestrogen plays a central role in keeping this process in balance . As oestrogen levels decline during the menopausal transition, bone resorption increases and bone formation may not keep pace, leading to a net loss of bone mineral density.

<div align="center">\n\n
![what happens to your muscle during menopause](osteoporosis-shepparton-physio-myo-podiatrist-exercise-pilates-bone.webp "600x400")\n\n
</div>

This imbalance is most pronounced around the final menstrual period and in the early years of menopause, when bone loss can be particularly rapid. The result, over time, can be a reduction in bone mineral density, the measure used to assess bone strength and fracture risk. 

Two clinical terms are important to understand here. Osteopenia means that bone mineral density is lower than the normal range for a young adult but has not reached the threshold for osteoporosis. Osteoporosis is a more significant reduction in bone density that increases the risk of fracture from relatively minor impacts. Neither condition typically causes pain or noticeable symptoms until a fracture has already occurred, which is why proactive assessment matters.

<span id="who-is-most-at-risk-of-bone-loss-around-menopause"></span>

### Who is most at risk of bone loss around menopause?

Bone loss during menopause affects most women to some degree, but certain factors increase the risk of more significant loss. In one large population study, almost 49% of postmenopausal women were found to have osteopenia, with prevalence varying by age, ethnicity and other factors.


##### Women who may be at higher risk include those with:


<div style="display: flex; justify-content: space-between; gap: 40px; text-align: left;">

  <div style="flex: 1;">
    <ul>
      <li>Early menopause (before the age of 45), including surgical menopause</li>
      <li>A previous fragility fracture (a fracture caused by a minor impact)</li>
      <li>A family history of osteoporosis or hip fracture</li>
    </ul>
  </div>

  <div style="flex: 1;">
    <ul>
      <li>Long-term corticosteroid use</li>
      <li>Low body weight</li>
      <li>A history of smoking or high alcohol intake</li>
      <li>Certain medical conditions, including coeliac disease and inflammatory bowel disease</li>
    </ul>
  </div>
</div>

<div align="center">\n\n
![why muscle matters more than you might think](risk-of-bone-loss-around-menopause.webp "600x400")\n\n
</div>

If one or more of these risk factors apply to you, a formal bone density scan and fracture risk assessment is particularly worth considering. LIPS Healthcare offers bone health assessment at our clinic in Battersea Power Station, London, accessible from Chelsea, Clapham, Pimlico, Vauxhall, Wandsworth and Nine Elms, and a short walk from Battersea Power Station station on the Northern line.

<span id="bone-density-scanning-in-london"></span>

### Bone density scanning in London: how LIPS Healthcare assesses your bone health

A bone density scan, sometimes called a DXA scan or DEXA scan, measures the mineral content of your bone at key sites such as the lumbar spine and femoral neck (hip). The result is expressed as a T-score, which compares your bone density to that of a healthy young adult and indicates whether bone mineral density scan results fall within the normal range, the osteopenia range, or the osteoporosis range.

<div align="center">\n\n
![How much protein do you actually need during menopause](echo-station.webp "600x400")\n\n
</div>

At LIPS Healthcare, bone health is assessed using REMS, Radiofrequency Echographic Multi-Spectrometry. REMS is a radiation-free, non-ionising ultrasound technology that analyses echographic signals from the lumbar spine and femoral neck to provide bone mineral density measurements and a skeletal fragility index. Recent research has demonstrated good agreement between REMS and conventional DXA scanning, making it a clinically validated alternative for bone health assessment and monitoring . 

REMS at LIPS Healthcare is particularly suitable for women who prefer to avoid radiation exposure, or who want a convenient bone density scan in London without a hospital referral. As with any investigation, results should always be interpreted within the wider clinical picture, including age, risk factors, medication history and overall fracture risk.

<div style="text-align:center;margin:2rem 0;">
<strong><a href="/bone-scan">Book a bone density scan</a></strong>
</div>

<span id="calcium-vitamin-d-and-protein-the-nutritional-foundations-of-bone-health"></span>

### Calcium, vitamin D and protein: the nutritional foundations of bone health

Nutrition cannot reverse established osteoporosis on its own, but it provides the essential building materials that bone requires throughout life. Three nutrients are central to this:

<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,280px),1fr));gap:24px;margin:2rem 0;width:100%;align-items:stretch;">

  <div style="min-width:0;border:1px solid #1A1A1A;border-radius:16px;background:#fff;padding:24px;">
    <div class="mb-4 flex h-[240px] w-full items-center justify-center overflow-hidden [&_span]:flex [&_span]:h-full [&_span]:w-full [&_span]:items-center [&_span]:justify-center [&_span]:overflow-hidden [&_img]:!h-auto [&_img]:!max-h-full [&_img]:!max-w-full [&_img]:object-contain">
      <img src="food-sources-calcium-isolated.webp" alt="Calcium-rich foods" title="480x240" />
    </div>
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;text-align:center;">Calcium</h4>
    <p style="margin:0 0 10px;font-size:14px;line-height:1.6;text-align:left;">Calcium is the principal mineral that gives bone its strength. The standard UK recommendation is a minimum of 700 mg per day, but several international menopause and osteoporosis organisations recommend total intakes closer to 1,000 to 1,200 mg per day for postmenopausal women . The priority is to obtain calcium from food wherever possible. Useful sources include:</p>
    <ul style="margin:0;padding-left:20px;font-size:14px;line-height:1.7;text-align:left;">
      <li>Milk, yoghurt and cheese</li>
      <li>Calcium-fortified plant-based drinks</li>
      <li>Tofu prepared with calcium</li>
      <li>Sardines and other fish eaten with soft bones</li>
      <li>Kale and other dark leafy green vegetables</li>
      <li>Sesame seeds, tahini, and almonds</li>
      <li>Pulses, beans and lentils</li>
    </ul>
  </div>

  <div style="min-width:0;border:1px solid #1A1A1A;border-radius:16px;background:#fff;padding:24px;">
    <div class="mb-4 flex h-[240px] w-full items-center justify-center overflow-hidden [&_span]:flex [&_span]:h-full [&_span]:w-full [&_span]:items-center [&_span]:justify-center [&_span]:overflow-hidden [&_img]:!h-auto [&_img]:!max-h-full [&_img]:!max-w-full [&_img]:object-contain">
      <img src="vitamin-d-supplement-health-capsule-with-yellow-granules-healthy-diet-symbol.webp" alt="Vitamin D" title="480x240" />
    </div>
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;text-align:center;">Vitamin D</h4>
    <p style="margin:0 0 10px;font-size:14px;line-height:1.6;text-align:left;">Vitamin D is essential for calcium absorption, bone mineralisation and muscle function. Status is assessed by measuring serum 25-hydroxyvitamin D [25(OH)D] in the blood. Requirements vary considerably depending on baseline levels, sun exposure, skin pigmentation, age and body composition.</p>
    <p style="margin:0;font-size:14px;line-height:1.6;text-align:left;">Research in postmenopausal and older adults suggests that maintenance intakes of approximately <span style="color:#1E2A8A; font-weight:700;">800 to 2,000 IU per day</span> are commonly used, particularly where vitamin D levels are low or deficiency risk is increased [1,2]. Higher doses may be needed to correct established deficiency, but this should be guided by a blood test and appropriate clinical advice. It is also important to note that very high vitamin D intakes do not necessarily improve bone outcomes and may increase fall risk in some older adults.</p>
  </div>

</div>
<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,280px),1fr));gap:24px;margin:2rem 0;width:100%;align-items:stretch;">

  <div style="min-width:0;border:1px solid #1A1A1A;border-radius:16px;background:#fff;padding:24px;">
    <div class="mb-4 flex h-[240px] w-full items-center justify-center overflow-hidden [&_span]:flex [&_span]:h-full [&_span]:w-full [&_span]:items-center [&_span]:justify-center [&_span]:overflow-hidden [&_img]:!h-auto [&_img]:!max-h-full [&_img]:!w-auto [&_img]:!max-w-full [&_img]:object-contain">
      <img src="balanced-diet-plate-a-vibrant-and-healthy-meal.webp" alt="Protein-rich foods" title="480x240" />
    </div>
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;text-align:center;">Protein</h4>
    <p style="margin:0;font-size:14px;line-height:1.6;text-align:left;">Bone contains a substantial protein matrix, the majority of which is collagen. Adequate protein intake therefore supports both muscle and bone health. Preserving muscle during menopause helps load bones in a healthy way, improves balance and may reduce fall risk. Protein, calcium and vitamin D are part of the same musculoskeletal picture and work best together.</p>
  </div>

  <div style="min-width:0;border:1px solid #1A1A1A;border-radius:16px;background:#fff;padding:24px;">
    <div class="mb-4 flex h-[240px] w-full items-center justify-center overflow-hidden [&_span]:flex [&_span]:h-full [&_span]:w-full [&_span]:items-center [&_span]:justify-center [&_span]:overflow-hidden [&_img]:!h-auto [&_img]:!max-h-full [&_img]:!w-auto [&_img]:!max-w-full [&_img]:object-contain">
      <img src="other-nutritions.webp" alt="Foods containing other important nutrients" title="480x240" />
    </div>
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;text-align:center;">Other nutrients</h4>
    <p style="margin:0;font-size:14px;line-height:1.6;text-align:left;">Bone metabolism also depends on magnesium, vitamin K, phosphorus, zinc, copper, manganese and boron. This does not mean every woman needs a large panel of supplements. A varied, nutrient-dense diet should remain the foundation, with targeted supplementation considered according to individual intake, health history and clinical need. As a Nutritional Therapy practitioner at LIPS Healthcare, I can help assess where genuine gaps exist and whether supplementation is appropriate for you.</p>
  </div>

</div>

<span id="why-your-bones-need-exercise-as-much-as-nutrition"></span>

### Why your bones need exercise as much as nutrition?

Nutrition provides the building materials, but bone responds to physical loading. Weight-bearing and resistance exercise place controlled stress on the skeleton and stimulate the processes involved in maintaining bone strength. Current UK osteoporosis guidance recommends a combination of weight-bearing and muscle-strengthening exercise for postmenopausal women at risk of osteoporosis or fragility fracture.

<div align="center">\n\n
![proteins alone is not enough](proteins-alone-is-not-enough-resized.webp "600x400")\n\n
</div>

Recent systematic reviews confirm that resistance training can improve bone mineral density at important sites including the lumbar spine, femoral neck and hip in postmenopausal women.

#### Activities that can contribute to bone health include:
- Resistance training with free weights or machines
- Body-weight exercises such as squats, lunges and step-ups
- Walking, stair climbing and hiking
- Dancing and appropriate impact exercise
- Balance work to reduce fall risk

Anyone with established osteoporosis, a previous fragility fracture or a significant falls history should seek professional guidance before beginning intensive or high-impact exercise. LIPS Healthcare offers access to a rehabilitation gym and physiotherapy at Battersea Power Station, London, so both nutrition and exercise can be addressed in one place. We see patients from Chelsea, Clapham, Pimlico, Vauxhall, Wandsworth and across south and central London.

<div style="text-align:center;margin:2rem 0;">
<strong><a href="/our-specialists/monica-durigon" target="_blank" rel="noopener noreferrer">Book a Nutritional Therapist</a></strong>
</div>

<span id="frequently-asked-questions"></span>
`,
      styles: {
        h3: 'text-lg md:text-[32px] font-bold text-theme-primary mb-4',
        h4: 'text-lg md:text-[20px] font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action',
        em: 'not-italic font-bold',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        table:
          'w-full table-fixed border-collapse text-xs md:text-sm border-2 border-black',
        th: 'border-2 border-black bg-text-muted-blue px-2 py-2 md:px-4 md:py-3 text-left font-semibold text-theme-primary whitespace-normal wrap-break-word',
        td: 'border-2 border-black px-2 py-2 md:px-4 md:py-3 align-top font-semibold whitespace-normal wrap-break-word',
      },
      tableOptions: {
        width: '100%',
        columnWidths: ['50%', '50%'],
      },
    },
    faq: {
      heading: {
        content: '#### Frequently asked questions (FAQS)',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'Does menopause cause osteoporosis?',
          },
          answer: {
            content:
              'Not inevitably, but menopause does accelerate bone loss in most women. As oestrogen levels fall, bone resorption increases and bone formation may not keep pace, reducing bone mineral density over time. Whether this leads to osteopenia or osteoporosis depends on the rate of loss, your starting bone density, and additional risk factors such as family history, smoking, body weight and medication use. Regular bone density scanning allows you to monitor changes and act before the threshold for osteoporosis is reached.',
          },
        },
        {
          question: {
            content: 'What does a bone density scan in London involve?',
          },
          answer: {
            content:
              'A bone density scan measures the mineral content of bone at key sites, typically the lumbar spine and femoral neck, and produces a T-score that indicates where your bone density sits relative to a healthy young adult. At LIPS Healthcare in Battersea Power Station, this is done using REMS technology, which is radiation-free and uses ultrasound to produce bone mineral density measurements. The scan is quick, non-invasive and requires no preparation. Results should always be interpreted alongside your clinical history and risk factors.',
          },
        },
        {
          question: {
            content: 'How much calcium should a woman take during menopause?',
          },
          answer: {
            content:
              'Most international menopause and osteoporosis organisations recommend a total calcium intake of 1,000 to 1,200 mg per day for postmenopausal women. The priority is to reach this through food rather than supplements. If dietary intake consistently falls short, a supplement can fill the gap, but very high doses are not recommended. As a Nutritional Therapy practitioner, I can review your current calcium intake from food and advise on whether supplementation is appropriate.',
          },
        },
        {
          question: {
            content: 'Do I need a vitamin D supplement during menopause?',
          },
          answer: {
            content:
              'Many women in the UK have vitamin D levels that are lower than optimal, particularly in winter months. Vitamin D is essential for calcium absorption and bone mineralisation, and deficiency increases fracture risk [1]. A blood test measuring serum 25-hydroxyvitamin D [25(OH)D] is the most reliable way to assess your status. Supplementation is commonly recommended for postmenopausal women, with maintenance doses of approximately 800 to 2,000 IU per day used in research settings, but the right dose for you depends on your current levels and other factors.',
          },
        },
        {
          question: {
            content: 'Can exercise really protect my bones during menopause?',
          },
          answer: {
            content:
              'Yes. Resistance training and weight-bearing exercise are among the most effective tools for maintaining bone mineral density at the lumbar spine, femoral neck and hip in postmenopausal women. Bone responds to mechanical loading, so the physical stimulus of exercise is essential alongside adequate nutrition. If you have established osteoporosis or a history of fractures, seek professional guidance on the most appropriate exercise for your situation before starting a new programme.',
          },
        },
        {
          question: {
            content: 'Is it worth getting a bone scan if I have no symptoms?',
          },
          answer: {
            content:
              'Yes, particularly if you are approaching or going through menopause and have one or more risk factors for bone loss. Bone loss is silent and does not cause pain until a fracture occurs. A bone density scan in London at LIPS Healthcare provides an objective measure of where your bone health currently stands, allows changes to be monitored over time, and can inform decisions about nutrition, exercise and whether medical review is needed. We see patients from Chelsea, Clapham, Vauxhall, Pimlico, Wandsworth and Nine Elms, and we are accessible via the Northern Line at Battersea Power Station station.',
          },
        },
      ],
    },
  },
];
